/**
 * 業務フローとステージの図の BigQuery 読取（API §4 `getFlow` / `listPjFlows`）。
 *
 * **フロー 1 本でもステージごと読む。** 型と値の流れの整合（制約 63）と入口・出口の相手
 * （制約 68 / 69）は、同じステージの主フローの業務と業務ルールを見ないと決まらない。読むのは
 * そのステージの業務フロー・その業務・業務ルール・変える先のプロパティまでで、グラフ全体は
 * 読まない。組み立ては `flow-view.ts` が持つ。
 *
 * CTE も相関サブクエリも `STRUCT IN UNNEST` も使わない（どちらも `CLAUDE.md` の BigQuery の罠）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 本とステージの図を master と案件適用後の姿で読み、案件が変えた業務フローを一覧にする
 * @graph-connects bigquery.spec_graph_nodes [reads_from, via:id] 業務フロー・ステージ・業務・業務ルール・名前の材料のノードの読取
 * @graph-connects bigquery.spec_graph_pj_changes [reads_from, via:pjNumber] 案件適用後の姿を作る変更の行と、案件が変えた業務フローの読取
 */
import { SPEC_ELEMENT_TABLE_IDS, FLOW_TYPES, } from '../../contract/elements/index.js';
import { collectLabelDependencyIds, composeElementSnapshot, decodeElementValue, deriveStageChains, indexElementGraph, stageChainPlaceOf, } from '../../core/elements/index.js';
import { asRecord, runReadQuery, tableRef } from '../read-client.js';
import { idOf, labelOf, nodeOf, recordOf, refOf, stageFlowIds } from './flow-fields.js';
import { buildFlowStageView, buildFlowView, collectFlowNeighborhood, stageChainViewOf, } from './flow-view.js';
/**
 * 近傍を読み足す段数の上限。
 *
 * ステージ → 業務フロー → 業務 → アクター → クラスの用語 → 属するクラス、と辿るので 4 段で
 * 揃う。**型を指定した全件読みにしない** — 図 1 枚のたびに用語・動詞・アクターを全件スキャン
 * することになる。段を数えずに繰り返すと、循環したデータで止まらなくなる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フローの読取が近傍（業務・業務ルール・名前の材料）を読み足す段数の上限
 * @graph-connects none
 */
export const FLOW_READ_ROUNDS = 4;
/**
 * 案件のどちらの面を読むかの 2 値。
 *
 * **読取の側が正である。** コンソールの入力スキーマ（`domain-spec-console` の
 * `ElementPjSideSchema`）はここを `z.enum` に渡して組む。両側で書き直すと、片方に面を足した
 * ときにもう片方が黙ってすり抜ける（構造的型付けでは型エラーにならない）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件のどちらの面を読むかの 2 値の閉集合
 * @graph-connects none
 */
export const STORY_FLOW_PJ_SIDES = ['before', 'after'];
/** BQ が返した値を整数にする。整数に読めなければ投げる。 @graph-connects none */
function readNumber(record, key) {
    const raw = record[key];
    const parsed = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isInteger(parsed)) {
        throw new Error(`spec-graph: ${key} に整数でない値が入っている: ${String(raw)}`);
    }
    return parsed;
}
/** BQ が返した値を文字列にする。 @graph-connects none */
function readText(record, key) {
    const raw = record[key];
    return typeof raw === 'string' ? raw : '';
}
/** 行を読み、読めなかった行を落とす。 @graph-connects none */
function records(rows) {
    return rows.flatMap((row) => {
        const record = asRecord(row);
        return record === null ? [] : [record];
    });
}
/**
 * `properties` 列の JSON を読む。**読めない値は握りつぶさず投げる。**
 *
 * この列を書いたのはこのシステム自身なので、読めない値はデータの不整合であって想定内の
 * 入力ではない。飛ばして続けると、欄の一部だけが欠けた「正しそうな姿」が図に出る。
 * @graph-connects none
 */
function parseProperties(raw) {
    if (raw === null || raw === undefined)
        return {};
    const record = asRecord(raw);
    if (record !== null)
        return record;
    if (typeof raw !== 'string') {
        throw new Error(`spec-graph: properties 列に読めない値が入っている: ${JSON.stringify(raw)}`);
    }
    const parsed = JSON.parse(raw);
    const decoded = asRecord(parsed);
    if (decoded === null) {
        throw new Error(`spec-graph: properties 列の JSON がオブジェクトでない: ${raw}`);
    }
    return decoded;
}
/** ノードの行を id → 値の写像にする。 @graph-connects none */
function toElementNodes(rows) {
    const nodes = new Map();
    for (const record of records(rows)) {
        nodes.set(readNumber(record, 'id'), {
            nodeType: readText(record, 'nodeType'),
            properties: parseProperties(record['properties']),
        });
    }
    return nodes;
}
/** ノードの行の更新日時を id → 日時の写像にする。 @graph-connects none */
function toUpdatedAt(rows) {
    return new Map(records(rows).map((record) => [readNumber(record, 'id'), readText(record, 'updatedAt')]));
}
/** 案件の変更の行を読む。 @graph-connects none */
function toPjChangeRows(rows) {
    return records(rows).map((record) => ({
        pjNumber: readNumber(record, 'pjNumber'),
        nodeId: readNumber(record, 'nodeId'),
        before: typeof record['before'] === 'string' ? record['before'] : null,
        after: typeof record['after'] === 'string' ? record['after'] : null,
        status: readText(record, 'status'),
        updatedAt: readText(record, 'updatedAt'),
        actor: readText(record, 'actor'),
    }));
}
/** 更新日時の列。 @graph-connects none */
const UPDATED_AT = "FORMAT_TIMESTAMP('%FT%H:%M:%E*SZ', updatedAt, 'UTC') AS updatedAt";
/** 案件の変更の行を読む SQL。`openOnly` なら確定済みを除く。 @graph-connects none */
function pjChangesSql(config, openOnly) {
    return `SELECT pjNumber, nodeId, before, after, status, ${UPDATED_AT}, actor
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.pjChanges)}
WHERE pjNumber = @pjNumber${openOnly ? " AND status != 'merged'" : ''}
ORDER BY nodeId`;
}
/** 番号でノードを読む。呼び出し側は空の並びを渡さない（段の読み足しは空なら先に止まる）。 @graph-connects none */
async function readByIds(config, nodeIds) {
    return toElementNodes(await runReadQuery(config, `SELECT id, nodeType, properties
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.nodes)}
WHERE graphId = @graphId AND id IN UNNEST(@nodeIds)`, { nodeIds }));
}
/**
 * ステージの入口か出口を、鎖の材料（業務と結末の行の組）として読む。欠ければ undefined。
 *
 * **欄が崩れていても投げない。** 見ているステージと無関係なステージの欄の崩れで、図そのものを
 * 倒さない（崩れは制約とリリースゲートが名指しする）。
 * @graph-connects idOf [calls] 欄の値を要素の id として読む
 * @graph-connects recordOf [calls] 欄の値をオブジェクトとして読む
 */
function stageEndingOf(value, field) {
    const row = recordOf(value.properties[field]) ?? {};
    const business = idOf(row, 'business');
    const ending = idOf(row, 'ending');
    return business === null || ending === null ? undefined : { business, ending };
}
/**
 * 姿の全ステージから鎖を導き、ステージ 1 件の置き場を引く。
 *
 * @param nodes 案件を重ねた姿（全ステージを含む）
 * @param stageId ステージの id
 * @returns 鎖の中の置き場
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 姿の全ステージの入口と出口から鎖を導き、ステージ 1 件の順・前・次を引く
 * @graph-connects deriveStageChains [calls] ステージ全体からの鎖の導出
 * @graph-connects stageChainPlaceOf [calls] ステージ 1 件の鎖の中の置き場
 * @graph-connects stageEndingOf [calls] 入口と出口の読み取り
 */
function chainPlaceOf(nodes, stageId) {
    const sources = new Map();
    for (const [id, value] of nodes) {
        if (value.nodeType !== 'Stage')
            continue;
        const entry = stageEndingOf(value, 'entry');
        const exit = stageEndingOf(value, 'exit');
        sources.set(id, {
            ...(entry === undefined ? {} : { entry }),
            ...(exit === undefined ? {} : { exit }),
        });
    }
    return stageChainPlaceOf(deriveStageChains(sources), stageId);
}
/** 置き場の前後のステージの番号。 @graph-connects none */
function chainNeighborIds(place) {
    return [...(place.prevStageId === null ? [] : [place.prevStageId]), ...place.nextStageIds];
}
/**
 * ステージ 1 つの近傍を読む。
 *
 * 1 本目でステージとその業務フロー、**鎖を導くための全ステージ**を master から引き、そこから
 * 近傍を段ごとに読み足す。鎖の前後のステージは名前の材料（クラスの用語と動詞）だけを読み足す —
 * 前後のステージの業務や業務フローは図に出ない。
 * **辿るのは案件を重ねた姿の上である** — 案件が書き換えた業務の `actor` / `rules` は重ねた後で
 * しか分からず、案件が起こした業務は master に行が無い。1 度問い合わせた番号は控えて再び
 * 聞かない（master に無い番号は何度読んでも返らない）。
 *
 * 最後に、業務ルールの変更の対象クラスに属するプロパティの用語を引く。制約 63 の値の流れは
 * 「変える先の値を取りうるプロパティ」で読むので、用語を番号では引けない。
 * @graph-connects collectFlowNeighborhood [calls] 近傍の番号を辿る
 * @graph-connects collectLabelDependencyIds [calls] 前後のステージの名前の材料
 * @graph-connects chainPlaceOf [calls] 鎖の中の置き場
 * @graph-connects readByIds [calls] 番号でノードを読む
 */
async function readStageNeighborhood(config, stageId, master, rows, updatedAt) {
    const stageRows = await runReadQuery(config, `SELECT id, nodeType, properties, ${UPDATED_AT}
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.nodes)}
WHERE graphId = @graphId AND (id = @stage OR nodeType = 'Stage' OR (nodeType = 'Flow' AND SAFE_CAST(JSON_VALUE(properties, '$.stage') AS INT64) = @stage))`, { stage: stageId });
    for (const [id, value] of toElementNodes(stageRows))
        master.set(id, value);
    for (const [id, at] of toUpdatedAt(stageRows))
        updatedAt.set(id, at);
    const requested = new Set(master.keys());
    for (let round = 0; round < FLOW_READ_ROUNDS; round += 1) {
        const composed = composeElementSnapshot(master, rows, () => []).nodes;
        const roots = [stageId, ...stageFlowIds(composed, stageId)];
        const neighbors = chainNeighborIds(chainPlaceOf(composed, stageId));
        const missing = [
            ...new Set([
                ...collectFlowNeighborhood(composed, roots),
                ...collectLabelDependencyIds(composed, neighbors),
            ]),
        ].filter((id) => !composed.has(id) && !requested.has(id));
        if (missing.length === 0)
            break;
        for (const id of missing)
            requested.add(id);
        for (const [id, value] of await readByIds(config, missing))
            master.set(id, value);
    }
    const beforeTerms = composeElementSnapshot(master, rows, () => []).nodes;
    const classIds = [
        ...new Set([...beforeTerms.values()].flatMap((value) => {
            if (value.nodeType !== 'Rule' || value.properties['kind'] !== 'mutation')
                return [];
            const target = idOf(recordOf(value.properties['body']) ?? {}, 'target');
            return target === null ? [] : [target];
        })),
    ].sort((left, right) => left - right);
    if (classIds.length > 0) {
        const termRows = await runReadQuery(config, `SELECT id, nodeType, properties
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.nodes)}
WHERE graphId = @graphId AND nodeType = 'Term' AND SAFE_CAST(JSON_VALUE(properties, '$.ownerClass') AS INT64) IN UNNEST(@classIds)`, { classIds });
        for (const [id, value] of toElementNodes(termRows))
            master.set(id, value);
    }
    for (const row of rows)
        updatedAt.set(row.nodeId, row.updatedAt);
    const composed = composeElementSnapshot(master, rows, () => []).nodes;
    return {
        composed,
        updatedAt,
        chain: stageChainViewOf(composed, chainPlaceOf(composed, stageId)),
    };
}
/**
 * 近傍を型ごとに読み分ける。**近傍の外のノードは入れない** — 案件の変更の行は近傍の外の
 * ノードも運んでくるが、それを読み分けると、図と無関係なノードの欄の不整合で図が倒れる。
 * @graph-connects collectFlowNeighborhood [calls] 近傍の番号
 * @graph-connects indexElementGraph [calls] 型ごとの読み分け
 */
function indexNeighborhood(composed, stageId) {
    const scope = new Set(collectFlowNeighborhood(composed, [stageId, ...stageFlowIds(composed, stageId)]));
    for (const [id, value] of composed) {
        if (value.nodeType === 'Term' && value.properties['section'] === 'property')
            scope.add(id);
    }
    return indexElementGraph(new Map([...composed].filter(([id]) => scope.has(id))));
}
/** 案件の変更の行を、`after` のときだけ読む。 @graph-connects pjChangesSql [calls] 変更の行の SQL */
async function readOverlayRows(config, pj) {
    if (pj === undefined || pj.side !== 'after')
        return [];
    return toPjChangeRows(await runReadQuery(config, pjChangesSql(config, true), { pjNumber: pj.number }));
}
/**
 * 業務フロー 1 本を読む。
 *
 * 案件を渡さない・`before` を渡したときは master だけを読む（変更ログを 1 度も引かない）。
 * `after` のときだけ案件の変更の行を引き、重ねてから組み立てる。
 *
 * @param config 読取接続
 * @param input フローの id と案件の指定
 * @returns フロー 1 本。フローが無い・契約の形に読めなければ null
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 本を master または案件適用後の姿で読み、型・入口・出口・レーン・節点・枝・役割の整合・違反に組んで返す
 * @graph-connects bigquery.spec_graph_nodes [reads_from, via:id] フローとその近傍のノードの読取
 * @graph-connects bigquery.spec_graph_pj_changes [reads_from, via:pjNumber] 案件適用後の姿を作る変更の行の読取
 * @graph-connects readStageNeighborhood [calls] フローのステージの近傍の読取
 * @graph-connects buildFlowView [calls] フロー 1 本の組み立て
 */
export async function readFlow(config, input) {
    const [flowRows, rows] = await Promise.all([
        runReadQuery(config, `SELECT id, nodeType, properties, ${UPDATED_AT}
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.nodes)}
WHERE graphId = @graphId AND id = @flowId`, { flowId: input.flowId }),
        readOverlayRows(config, input.pj),
    ]);
    const master = toElementNodes(flowRows);
    const flow = nodeOf(composeElementSnapshot(master, rows, () => []).nodes, input.flowId, 'Flow');
    const stageId = flow === null ? null : idOf(flow.properties, 'stage');
    if (stageId === null)
        return null;
    const neighborhood = await readStageNeighborhood(config, stageId, master, rows, toUpdatedAt(flowRows));
    return buildFlowView(indexNeighborhood(neighborhood.composed, stageId), input.flowId, neighborhood.updatedAt.get(input.flowId) ?? '', neighborhood.chain.next);
}
/**
 * ステージの図 1 枚を読む。案件の扱いは `readFlow` と同じである。
 *
 * @param config 読取接続
 * @param input ステージの id と案件の指定
 * @returns ステージの図。ステージが無ければ null
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business ステージ 1 つを master または案件適用後の姿で読み、出口・主フロー・支援フローの札に組んで返す
 * @graph-connects bigquery.spec_graph_nodes [reads_from, via:id] ステージとその近傍のノードの読取
 * @graph-connects bigquery.spec_graph_pj_changes [reads_from, via:pjNumber] 案件適用後の姿を作る変更の行の読取
 * @graph-connects readStageNeighborhood [calls] ステージの近傍の読取
 * @graph-connects buildFlowStageView [calls] ステージの図の組み立て
 */
export async function readFlowStage(config, input) {
    const rows = await readOverlayRows(config, input.pj);
    const neighborhood = await readStageNeighborhood(config, input.stageId, new Map(), rows, new Map());
    return buildFlowStageView(indexNeighborhood(neighborhood.composed, input.stageId), input.stageId, (id) => neighborhood.updatedAt.get(id) ?? '', neighborhood.chain);
}
/** 値の型を読む。語彙に無ければ null。 @graph-connects none */
function flowTypeOf(value) {
    return FLOW_TYPES.find((type) => type === value.properties['type']) ?? null;
}
/**
 * 案件が変えた業務フローを読む。
 *
 * 案件の変更の行（確定済みも含む）のうち、`before` か `after` の型が業務フローのものを返す。
 * 変更の種類は `before` / `after` の null から導く。ラベルとステージは `after`（削除なら
 * `before`）の値から、名前の材料は master に案件を重ねた姿から組む。
 *
 * @param config 読取接続
 * @param pjNumber 案件番号
 * @returns 案件が変えた業務フロー（フローの id の昇順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件が変えた業務フローを、変更の種類（新規・更新・削除）・型・ステージ・ラベルつきで一覧にする
 * @graph-connects bigquery.spec_graph_pj_changes [reads_from, via:pjNumber] 案件の変更の行の読取
 * @graph-connects bigquery.spec_graph_nodes [reads_from, via:id] 名前の材料のノードの読取
 * @graph-connects readByIds [calls] 番号でノードを読む
 * @graph-connects collectLabelDependencyIds [calls] ラベルの材料の収集
 */
export async function listPjFlowChanges(config, pjNumber) {
    const rows = toPjChangeRows(await runReadQuery(config, pjChangesSql(config, false), { pjNumber }));
    const flows = rows.flatMap((row) => {
        const json = row.after ?? row.before;
        if (json === null)
            return [];
        const value = decodeElementValue(`案件 ${String(pjNumber)} のノード ${String(row.nodeId)}`, json);
        if (value.nodeType !== 'Flow')
            return [];
        const change = row.before === null ? 'added' : row.after === null ? 'deleted' : 'updated';
        return [{ nodeId: row.nodeId, value, change }];
    });
    if (flows.length === 0)
        return [];
    const overlay = rows.filter((row) => row.status !== 'merged');
    const master = new Map();
    const requested = new Set();
    const snapshot = () => {
        const composed = new Map(composeElementSnapshot(master, overlay, () => []).nodes);
        for (const flow of flows)
            composed.set(flow.nodeId, flow.value);
        return composed;
    };
    const flowIds = flows.map((flow) => flow.nodeId);
    for (let round = 0; round < FLOW_READ_ROUNDS; round += 1) {
        const composed = snapshot();
        const missing = collectLabelDependencyIds(composed, [...flowIds, ...requested]).filter((id) => !requested.has(id));
        if (missing.length === 0)
            break;
        for (const id of missing)
            requested.add(id);
        for (const [id, value] of await readByIds(config, missing))
            master.set(id, value);
    }
    const composed = snapshot();
    return flows.map((flow) => {
        const stageId = idOf(flow.value.properties, 'stage');
        return {
            flowId: flow.nodeId,
            label: labelOf(composed, flow.nodeId),
            type: flowTypeOf(flow.value),
            stage: stageId === null ? null : refOf(composed, stageId),
            change: flow.change,
        };
    });
}
//# sourceMappingURL=flow-read.js.map